// ─────────────────────────────────────────────────────────────────────────────
// GENERATED FILE — DO NOT EDIT HERE.
//
// Source of truth: invits-app-new/shared/eventDateTime.js
// Regenerate:     npm run sync:shared   (from invits-app-new)
//
// Editing this copy reintroduces exactly the app/web divergence that shared/
// exists to eliminate. Change the source, then re-run the sync.
// ─────────────────────────────────────────────────────────────────────────────

// shared/eventDateTime.js
//
// Date/time rules for creating an event. Pure, so both the app and the website
// validate identically and so this can be tested without a picker on screen.
//
// ── The bugs this fixes ────────────────────────────────────────────────────
//
// 1. "Same start and end date locks the time picker at 5:30 am."
//
//    5:30 is the tell: IST is UTC+05:30, so a date-only value (midnight UTC)
//    renders as 05:30 local. Passing such a value as a picker BOUND produced a
//    range whose maximum landed before its minimum, and the picker collapsed to
//    a single unusable instant.
//
//    The real rule is not about dates at all: a same-day event is perfectly
//    normal, and only the COMBINED date+time of the end may not precede the
//    combined date+time of the start. So the pickers get no cross-field bounds;
//    the user picks freely and validation runs afterwards, which is what
//    `validateEventWindow` below is for.
//
// 2. "RSVP deadline can't be the same date as the event."
//
//    maximumDate was the raw start date (midnight) while minimumDate was `now`.
//    For an event today that is max < min - again an empty range. A deadline on
//    the event day is legitimate; it just has to fall before the event starts.

/** Combines a date-carrying value and a time-carrying value into one Date. */
export function combineDateAndTime(date, time) {
  if (!date) return null;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return null;

  if (time) {
    const t = new Date(time);
    if (!Number.isNaN(t.getTime())) {
      d.setHours(t.getHours(), t.getMinutes(), 0, 0);
      return d;
    }
  }
  // No usable time: treat as the start of that day in LOCAL time. Using local
  // midnight rather than the raw value is what avoids the UTC-offset shift.
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Local start of day. */
/**
 * Parses a date-only string as LOCAL midnight.
 *
 * `new Date('2026-09-20')` is specified to parse a bare date as UTC, so in
 * India it becomes 05:30 local — which is where the "start time always shows
 * 5:30 am" report comes from. Every event loaded for editing had its date
 * shifted half a day into the morning, and any time derived from that date
 * inherited the offset.
 *
 * Datetime strings (containing 'T' or a space and a colon) are left to the
 * platform, since those carry their own offset and are not ambiguous.
 *
 * @param {string|Date|null|undefined} value
 * @returns {Date|null}
 */
export function parseLocalDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value !== 'string') return null;

  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (dateOnly) {
    const [, y, m, d] = dateOnly;
    return new Date(Number(y), Number(m) - 1, Number(d));
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function startOfDay(date) {
  const d = new Date(date ?? Date.now());
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Local end of day — the last instant that still counts as the same date. */
export function endOfDay(date) {
  const d = new Date(date ?? Date.now());
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(23, 59, 59, 999);
  return d;
}

export function isSameDay(a, b) {
  if (!a || !b) return false;
  const x = new Date(a);
  const y = new Date(b);
  if (Number.isNaN(x.getTime()) || Number.isNaN(y.getTime())) return false;
  return (
    x.getFullYear() === y.getFullYear() &&
    x.getMonth() === y.getMonth() &&
    x.getDate() === y.getDate()
  );
}

/**
 * Validates the event window AFTER the user has chosen freely.
 *
 * Deliberately not enforced through picker bounds: constraining the pickers is
 * what produced the locked, unusable range. Let both values be picked, then
 * explain what is wrong.
 *
 * @returns {{valid: boolean, errors: Record<string,string>}}
 */
export function validateEventWindow({ startDate, startTime, endDate, endTime }) {
  const errors = {};

  if (!startDate) {
    errors.startDate = 'Pick a start date.';
    return { valid: false, errors };
  }

  const start = combineDateAndTime(startDate, startTime);
  if (!start) {
    errors.startDate = 'That start date is not valid.';
    return { valid: false, errors };
  }

  // An end is optional — plenty of events have no stated finish.
  if (!endDate && !endTime) return { valid: true, errors };

  // A time with no date means "same day", which is the common case.
  const end = combineDateAndTime(endDate ?? startDate, endTime);
  if (!end) {
    errors.endDate = 'That end date is not valid.';
    return { valid: false, errors };
  }

  if (end.getTime() === start.getTime()) {
    errors.endTime = 'The end time is the same as the start time.';
    return { valid: false, errors };
  }

  if (end.getTime() < start.getTime()) {
    // Same-day is not the problem; the ordering is. Say which.
    errors.endTime = isSameDay(start, end)
      ? 'The end time is before the start time.'
      : 'The event ends before it starts.';
    return { valid: false, errors };
  }

  return { valid: true, errors };
}

/**
 * Validates the RSVP deadline.
 *
 * Same day as the event is allowed — it only has to be before the event begins.
 */
export function validateRsvpDeadline({ rsvpDeadline, startDate, startTime, now = new Date() }) {
  const errors = {};
  if (!rsvpDeadline) {
    errors.rsvpDeadline = 'Pick an RSVP deadline.';
    return { valid: false, errors };
  }

  const deadlineEnd = endOfDay(rsvpDeadline);
  if (!deadlineEnd) {
    errors.rsvpDeadline = 'That date is not valid.';
    return { valid: false, errors };
  }

  // Compared against the END of the deadline day, so choosing today is fine.
  if (deadlineEnd.getTime() < new Date(now).getTime()) {
    errors.rsvpDeadline = 'That date has already passed.';
    return { valid: false, errors };
  }

  const start = combineDateAndTime(startDate, startTime);
  if (start) {
    // Same day as the event is fine; the deadline only has to precede the
    // event's actual start instant.
    const deadlineStart = startOfDay(rsvpDeadline);
    if (deadlineStart.getTime() > start.getTime()) {
      errors.rsvpDeadline = 'The deadline is after the event starts.';
      return { valid: false, errors };
    }
  }

  return { valid: true, errors };
}

/**
 * Safe bounds for the RSVP deadline picker.
 *
 * Both are whole days, and `max` is the END of the event day, so an event today
 * still leaves a usable range instead of min > max.
 */
export function rsvpDeadlineBounds({ startDate, now = new Date() }) {
  const min = startOfDay(now);
  const eventEnd = startDate ? endOfDay(startDate) : null;
  // A year out is only a fallback for when no start date has been chosen yet.
  const fallback = endOfDay(new Date(new Date(now).getTime() + 365 * 24 * 60 * 60 * 1000));
  let max = eventEnd ?? fallback;

  // Never hand a picker an inverted range, whatever the inputs.
  if (max.getTime() < min.getTime()) max = endOfDay(min);

  return { min, max };
}
